import Network from "../network/network";
import PersonService from "./PersonService";
import InstitutionService from "./InstitutionService";
import PersonToInstitutionRelationService from "./PersonToInstitutionRelationService";
import PersonToInstRelationTypeService from "./PersonToInstRelationTypeService";
import SalutationService from "./SalutationService";
import AddressParserService from "./AddressParserService";
import ContactMediaService from "./ContactMediaService";
import { IAddressParserResult } from "../interfaces/IAddressParserResult";
import Util from "../util/Util";

export default class ContactCreationService extends kendo.data.ObservableObject {
    private ptirTypeService: PersonToInstRelationTypeService;
    private salutationService: SalutationService;
    private addressParserService: AddressParserService;
    private translate: (key: string, args?: any) => string;

    // UI State
    private cropperInstance: any;
    private selectedPhotoFile: File;
    private currentDraggedChip: any;
    private currentSelectedChip: any;

    private onContactCreated: (personId: number | null, institutionId: number | null) => Promise<void>;
    private showNotification: (message: string, level: "warning" | "success" | "error") => void;

    constructor(
        ptirTypeService: PersonToInstRelationTypeService,
        salutationService: SalutationService,
        addressParserService: AddressParserService,
        translate: (key: string, args?: any) => string,
        onContactCreated?: (personId: number | null, institutionId: number | null) => Promise<void>,
        showNotification?: (message: string, level: "warning" | "success" | "error") => void
    ) {
        super();
        super.init(this);
        
        this.ptirTypeService = ptirTypeService;
        this.salutationService = salutationService;
        this.addressParserService = addressParserService;
        this.translate = translate;
        this.onContactCreated = onContactCreated;
        this.showNotification = showNotification || ((msg) => alert(msg)); // Fallback to alert

        this.initializeState();
        this.setupInlineFormDelegation();
    }

    public get<T>(name: string): T {
        return super.get(name) as T;
    }

    private t(key: string): string {
        return this.translate(key);
    }

    private initializeState() {
        this.set("createContactPhotoVisible", false);
        this.set("createContactTextVisible", false);
        this.set("selectedPhotoFile", null);
        this.set("canAnalyzePhoto", false);
        this.set("canAnalyzeText", false);
        this.set("contactTextInput", "");
        this.set("createContactReviewVisible", false);
        this.set("parsedPersons", []);
        this.set("parsedInstitutions", []);
        this.set("parsedRelation", null);
        this.set("hasRelation", false);
        this.set("unassignedContactData", []);
        this.set("canCreateContact", false);
        this.set("currentInlineType", "email");
        this.set("currentInlineContext", null);
        this.set("relationTypes", []);
        this.set("selectedRelationTypeId", null);
        this.set("salutations", []);
        this.set("selectedFormalityId", null);
        this.set("canAddRelation", false);
    }

    // === Photo Analysis ===

    public get canAnalyzePhoto(): boolean {
        return !!this.selectedPhotoFile;
    }

    public selectPhoto = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = (e: any) => {
            const file = e.target.files[0];
            if (file) {
                this.selectedPhotoFile = file;
                this.set("selectedPhotoFile", file);
                this.trigger("change", { field: "canAnalyzePhoto" });
                this.showCropper(file);
            }
        };
        input.click();
    };

    private showCropper(file: File) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
            const imageElement = document.getElementById("photo-crop-image") as HTMLImageElement;
            if (imageElement) {
                imageElement.src = e.target.result;
                imageElement.style.display = "block";

                if (this.cropperInstance) {
                    this.cropperInstance.destroy();
                }

                // @ts-ignore - Cropper is loaded via CDN
                this.cropperInstance = new Cropper(imageElement, {
                    aspectRatio: NaN,
                    viewMode: 2,
                    autoCropArea: 1,
                    zoomable: false,
                    zoomOnWheel: false,
                    zoomOnTouch: false,
                    scalable: false,
                    responsive: true,
                    restore: false,
                    center: true,
                    highlight: true,
                    cropBoxMovable: true,
                    cropBoxResizable: true,
                    toggleDragModeOnDblclick: false,
                });
            }
        };
        reader.readAsDataURL(file);
    }

    public analyzePhoto = async () => {
        if (!this.cropperInstance) {
            this.showNotification(this.t("error-cropper-not-loaded"), "error");
            return;
        }

        try {
            Util.showLoading();

            const canvas = this.cropperInstance.getCroppedCanvas();
            const blob = await new Promise<Blob>((resolve) => canvas.toBlob(resolve));

            const formData = new FormData();
            formData.append("file", blob, "cropped-image.png");

            const result: IAddressParserResult = await this.addressParserService.extractFromImage(blob);
            
            // Clear photo BEFORE showing review
            this.clearPhoto();
            
            await this.showContactReview(result);

            Util.hideLoading();
        } catch (error) {
            Util.hideLoading();
            console.error("Error analyzing photo:", error);
            this.showNotification(this.t("error-analyzing-photo"), "error");
        }
    };

    private clearPhoto() {
        if (this.cropperInstance) {
            this.cropperInstance.destroy();
            this.cropperInstance = null;
        }
        this.selectedPhotoFile = null;
        this.set("selectedPhotoFile", null);
        this.set("canAnalyzePhoto", false);
        
        const imageElement = document.getElementById("photo-crop-image") as HTMLImageElement;
        if (imageElement) {
            imageElement.src = "";
            imageElement.style.display = "none";
            imageElement.removeAttribute("src");
        }
    }

    public cancelCreateFromPhoto = () => {
        this.clearPhoto();
        this.set("createContactPhotoVisible", false);
    };

    // === Text Analysis ===

    public get canAnalyzeText(): boolean {
        const text = this.get<string>("contactTextInput");
        return text && text.trim().length > 0;
    }

    public analyzeText = async () => {
        // Get text directly from DOM to avoid binding issues
        const textarea = document.getElementById("contactTextInput") as HTMLTextAreaElement;
        const text = textarea ? textarea.value : "";
        
        if (!text || text.trim().length === 0) {
            // Simply do nothing if empty
            return;
        }

        try {
            Util.showLoading();

            const result: IAddressParserResult = await this.addressParserService.extractFromText(text);
            
            await this.showContactReview(result);
            
            // Clear textarea directly
            if (textarea) {
                textarea.value = "";
            }
            this.set("contactTextInput", "");

            Util.hideLoading();
        } catch (error) {
            Util.hideLoading();
            console.error("Error analyzing text:", error);
            this.showNotification(this.t("error-analyzing-text"), "error");
        }
    };

    public cancelCreateFromText = () => {
        this.set("createContactTextVisible", false);
        
        // Clear textarea directly
        const textarea = document.getElementById("contactTextInput") as HTMLTextAreaElement;
        if (textarea) {
            textarea.value = "";
        }
        this.set("contactTextInput", "");
    };

    // === Contact Review ===

    private async showContactReview(result: IAddressParserResult) {
        const placeholders = {
            title: this.t("title"),
            firstName: this.t("first-name"),
            lastName: this.t("last-name"),
            institutionName: this.t("institution-name"),
            street: this.t("street"),
            houseNumber: this.t("house-number"),
            postcode: this.t("postcode"),
            city: this.t("city"),
        };

        const labels = {
            person: this.t("person"),
            institution: this.t("institution"),
            relation: this.t("relation"),
            sexUnspecified: this.t("sex-unspecified"),
            sexMale: this.t("sex-male"),
            sexFemale: this.t("sex-female"),
        };

        // Prepare persons (sex: 0=unspecified, 1=male, 2=female)
        // Only take the first person if multiple are found
        const persons = result.Names.length > 0 ? [result.Names[0]].map(name => ({
            title: name.Title || "",
            firstName: name.First || "",
            lastName: name.Last || "",
            sex: 0,
            contactData: [],
            placeholders: placeholders,
            labels: labels,
            address: null,
        })) : [];

        // Prepare institutions
        // Only take the first institution if multiple are found
        const institutions = result.Organizations.length > 0 ? [result.Organizations[0]].map(org => ({
            name: org,
            contactData: [],
            placeholders: placeholders,
            labels: labels,
            address: null,
        })) : [];

        // Prepare unassigned contact data chips
        const contactChips = [
            ...result.Emails.map(email => ({
                type: "email",
                value: email,
                displayValue: email,
                icon: "fa-envelope",
            })),
            ...result.PhoneNumbers.map(phone => ({
                type: "phone",
                value: phone,
                displayValue: phone,
                icon: "fa-phone",
            })),
            ...result.Urls.map(url => ({
                type: "url",
                value: url,
                displayValue: url,
                icon: "fa-globe",
            })),
        ];

        // Prepare address
        const address =
            result.StructuredAddresses.length > 0
                ? {
                      street: result.StructuredAddresses[0].Road || "",
                      houseNumber: result.StructuredAddresses[0].HouseNumber || "",
                      postcode: result.StructuredAddresses[0].Postcode || "",
                      city: result.StructuredAddresses[0].City || "",
                  }
                : null;

        // Default assignment logic - SIMPLIFIED UI VERSION
        // UI: Assign to Person (if exists) else Institution
        // Backend: If both exist, relation is created automatically in buildResultJson
        let relation = null;
        let hasRelation = false;
        let unassignedChips: any[] = [];
        
        // Helper: assign first of each type to target, collect rest
        const assignFirstOfEachType = (target: any, chips: any[], collectUnassigned: boolean = true) => {
            const assigned: any[] = [];
            const types = ['email', 'phone', 'url'];
            
            types.forEach(type => {
                const matching = chips.filter(c => c.type === type);
                if (matching.length > 0) {
                    assigned.push(matching[0]); // Take first
                    if (collectUnassigned && matching.length > 1) {
                        unassignedChips.push(...matching.slice(1)); // Rest to unassigned
                    }
                }
            });
            
            target.contactData = assigned;
        };
        
        if (persons.length > 0 && institutions.length > 0) {
            // Both person and institution exist
            // UI: Show contact data at Person
            // Backend: Will be moved to relation in buildResultJson (except URL)
            
            const urlChips = contactChips.filter(c => c.type === 'url');
            const nonUrlChips = contactChips.filter(c => c.type !== 'url');
            
            // URLs → Institution (as before)
            if (urlChips.length > 0) {
                institutions[0].contactData.push(urlChips[0]);
                if (urlChips.length > 1) {
                    unassignedChips.push(...urlChips.slice(1));
                }
            }
            
            // Email/Phone → Person (in UI)
            assignFirstOfEachType(persons[0], nonUrlChips, true);
            
            // Set hasRelation flag for backend processing
            hasRelation = true;
            relation = {
                contactData: [],
                labels: labels,
            };
            
        } else if (persons.length > 0 && institutions.length === 0) {
            // Only person → assign all to person
            assignFirstOfEachType(persons[0], contactChips, true);
            
        } else if (persons.length === 0 && institutions.length > 0) {
            // Only institution → assign all to institution
            assignFirstOfEachType(institutions[0], contactChips, true);
            
        } else {
            // Nothing parsed → add empty person for manual input
            const labels = {
                person: this.t("person"),
                institution: this.t("institution"),
                relation: this.t("relation"),
                sexUnspecified: this.t("sex-unspecified"),
                sexMale: this.t("sex-male"),
                sexFemale: this.t("sex-female"),
            };
            persons.push({
                title: "",
                firstName: "",
                lastName: "",
                sex: 0,
                contactData: [],
                placeholders: placeholders,
                labels: labels,
                address: null,
            } as any);
        }

        // Assign address to correct entity based on rules
        if (address) {
            // Standard: Person + Institution → Institution, sonst was vorhanden ist
            if (persons.length > 0 && institutions.length > 0) {
                // Zur Institution
                (institutions[0] as any).address = address;
            } else if (persons.length > 0) {
                // Zur Person
                (persons[0] as any).address = address;
            } else if (institutions.length > 0) {
                // Zur Institution
                (institutions[0] as any).address = address;
            }
        }

        // Add hasInstitution flag to each person BEFORE setting as observable
        const hasInstitution = institutions.length > 0;
        persons.forEach((p: any) => {
            // Set property on plain object (not yet an observable)
            if (typeof p.set === 'function') {
                // Already an observable
                p.set("hasInstitution", hasInstitution);
            } else {
                // Plain object
                p.hasInstitution = hasInstitution;
            }
        });

        // Load relation types BEFORE setting observables if both person and institution exist
        if (hasInstitution && persons.length > 0) {
            await this.loadRelationTypes();
        }

        // Load salutations if person exists
        if (persons.length > 0) {
            await this.loadSalutations();
        }

        this.set("parsedPersons", persons);
        this.set("parsedInstitutions", institutions);
        this.set("parsedRelation", relation);
        this.set("hasRelation", hasRelation);
        this.set("unassignedContactData", unassignedChips);
        this.updateCanCreateContact();

        // Hide photo/text dialogs and show review
        this.set("createContactPhotoVisible", false);
        this.set("createContactTextVisible", false);
        this.set("createContactReviewVisible", true);
        
        // Setup chip assignment menu event listeners
        setTimeout(() => this.setupChipAssignmentMenu(), 100);
    }
    
    private setupChipAssignmentMenu() {
        const personItem = document.getElementById("assignToPerson");
        const institutionItem = document.getElementById("assignToInstitution");
        
        if (personItem) {
            // Remove old listener if exists
            const newPersonItem = personItem.cloneNode(true) as HTMLElement;
            personItem.parentNode?.replaceChild(newPersonItem, personItem);
            newPersonItem.addEventListener("click", (e) => this.assignChipToPerson(e));
        }
        
        if (institutionItem) {
            // Remove old listener if exists
            const newInstitutionItem = institutionItem.cloneNode(true) as HTMLElement;
            institutionItem.parentNode?.replaceChild(newInstitutionItem, institutionItem);
            newInstitutionItem.addEventListener("click", (e) => this.assignChipToInstitution(e));
        }
    }

    private async loadRelationTypes() {
        try {
            const types = await this.ptirTypeService.getItems();
            const mapped = types.map(t => ({
                id: t.Id.toString(),
                name: t.TranslatedDisplayName || t.Id.toString()
            }));
            this.set("relationTypes", mapped);
            // Set default relation type
            if (mapped.length > 0) {
                this.set("selectedRelationTypeId", mapped[0].id);
            }
        } catch (error) {
            console.error("Error loading relation types:", error);
            this.set("relationTypes", []);
        }
    }

    private async loadSalutations() {
        try {
            const salutations = await this.salutationService.getItems();
            const mapped = salutations.map(s => ({
                id: s.Id.toString(),
                name: s.TranslatedNameWithPlaceholder || s.Id.toString()
            }));
            this.set("salutations", mapped);
            // Set default salutation (first one)
            if (mapped.length > 0) {
                this.set("selectedFormalityId", mapped[0].id);
            }
        } catch (error) {
            console.error("Error loading salutations:", error);
            this.set("salutations", []);
        }
    }

    public cancelContactReview = () => {
        this.set("createContactReviewVisible", false);
    };

    // === Drag & Drop ===

    public handleDragStart = (e: any) => {
        // Get chip data from element's data attributes
        const element = e.currentTarget || e.target;
        const $element = $(element);
        
        this.currentDraggedChip = {
            type: $element.data('chip-type'),
            value: $element.data('chip-value'),
            icon: $element.data('chip-icon'),
            displayValue: $element.data('chip-displayvalue')
        };
        
        console.log("Drag started with chip:", this.currentDraggedChip);
    };

    public handleDragOver = (e: any) => {
        e.preventDefault();
        const dropZone = e.currentTarget;
        const targetType = dropZone.getAttribute("data-target");
        const chip = this.currentDraggedChip;
        
        if (!chip || !targetType) {
            dropZone.classList.add("drag-over");
            return;
        }
        
        // Get the actual target object based on type
        let targetObject = null;
        if (targetType === "person") {
            const persons = this.toNativeArray(this.get("parsedPersons"));
            targetObject = persons.length > 0 ? persons[0] : null;
        } else if (targetType === "institution") {
            const institutions = this.toNativeArray(this.get("parsedInstitutions"));
            targetObject = institutions.length > 0 ? institutions[0] : null;
        } else if (targetType === "relation") {
            targetObject = this.get<any>("parsedRelation");
        }
        
        // Check if type already exists in target
        const forbidden = targetObject && this.hasChipOfType(targetObject, chip.type);
        const forbiddenIcon = dropZone.querySelector(".drop-zone-forbidden-icon");
        
        if (forbidden) {
            dropZone.classList.add("drag-over-forbidden");
            dropZone.classList.remove("drag-over");
            if (forbiddenIcon) forbiddenIcon.style.display = "flex";
        } else {
            dropZone.classList.add("drag-over");
            dropZone.classList.remove("drag-over-forbidden");
            if (forbiddenIcon) forbiddenIcon.style.display = "none";
        }
    };

    public handleDragLeave = (e: any) => {
        const dropZone = e.currentTarget;
        const forbiddenIcon = dropZone.querySelector(".drop-zone-forbidden-icon");
        
        dropZone.classList.remove("drag-over");
        dropZone.classList.remove("drag-over-forbidden");
        if (forbiddenIcon) forbiddenIcon.style.display = "none";
    };

    public handleDropToPerson = (e: any) => {
        e.preventDefault();
        const dropZone = e.currentTarget;
        const forbiddenIcon = dropZone.querySelector(".drop-zone-forbidden-icon");
        
        dropZone.classList.remove("drag-over");
        dropZone.classList.remove("drag-over-forbidden");
        if (forbiddenIcon) forbiddenIcon.style.display = "none";

        const personData = e.data;
        this.moveChipToObject(personData);
    };

    public handleDropToInstitution = (e: any) => {
        e.preventDefault();
        const dropZone = e.currentTarget;
        const forbiddenIcon = dropZone.querySelector(".drop-zone-forbidden-icon");
        
        dropZone.classList.remove("drag-over");
        dropZone.classList.remove("drag-over-forbidden");
        if (forbiddenIcon) forbiddenIcon.style.display = "none";

        const institutionData = e.data;
        this.moveChipToObject(institutionData);
    };

    public handleChipClick = (e: any) => {
        this.currentSelectedChip = e.data;
    };

    public showChipAssignmentMenu = (e: any) => {
        // Prevent event propagation
        const event = e.originalEvent || e;
        if (event.stopPropagation) event.stopPropagation();
        if (event.preventDefault) event.preventDefault();
        
        // Store the chip that was clicked
        this.currentSelectedChip = e.data;
        
        // Get the chip element
        const chipElement = event.currentTarget || event.target;
        
        // Get menu element
        const menu = document.getElementById("chipAssignmentMenu");
        if (!menu) {
            console.warn("chipAssignmentMenu not found");
            return;
        }
        
        // Show/hide menu items based on availability and validation
        const persons = this.get("parsedPersons") as any;
        const institutions = this.get("parsedInstitutions") as any;
        
        const chipType = e.data.type;
        
        const personItem = document.getElementById("assignToPerson");
        const institutionItem = document.getElementById("assignToInstitution");
        
        // Show person option only if person exists AND doesn't already have this chip type
        if (personItem) {
            const firstPerson = persons && persons.length > 0 ? (persons.at ? persons.at(0) : persons[0]) : null;
            const canAssignToPerson = firstPerson && !this.hasChipOfType(firstPerson, chipType);
            personItem.style.display = canAssignToPerson ? "flex" : "none";
        }
        
        // Show institution option only if institution exists AND doesn't already have this chip type
        if (institutionItem) {
            const firstInstitution = institutions && institutions.length > 0 ? (institutions.at ? institutions.at(0) : institutions[0]) : null;
            const canAssignToInstitution = firstInstitution && !this.hasChipOfType(firstInstitution, chipType);
            institutionItem.style.display = canAssignToInstitution ? "flex" : "none";
        }
        
        // Position menu near the clicked chip (above to prevent scrollbar)
        const rect = chipElement.getBoundingClientRect();
        menu.style.display = "block";
        menu.style.left = rect.left + "px";
        menu.style.top = (rect.top - menu.offsetHeight - 5) + "px";
        menu.style.bottom = "auto";
        
        // Close menu when clicking outside
        const closeMenu = (ev: MouseEvent) => {
            if (!menu.contains(ev.target as Node)) {
                menu.style.display = "none";
                document.removeEventListener("click", closeMenu);
            }
        };
        setTimeout(() => document.addEventListener("click", closeMenu), 10);
    };

    public assignChipToPerson = (e?: Event) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        console.log("assignChipToPerson called");
        const persons = this.get("parsedPersons") as any;
        console.log("Persons observable:", persons);
        if (persons && persons.length > 0) {
            const firstPerson = persons.at ? persons.at(0) : persons[0];
            console.log("Moving chip to person:", firstPerson);
            this.moveChipToObject(firstPerson);
        }
        // Hide menu
        const menu = document.getElementById("chipAssignmentMenu");
        if (menu) menu.style.display = "none";
    };

    public assignChipToInstitution = (e?: Event) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        console.log("assignChipToInstitution called");
        const institutions = this.get("parsedInstitutions") as any;
        console.log("Institutions observable:", institutions);
        if (institutions && institutions.length > 0) {
            const firstInstitution = institutions.at ? institutions.at(0) : institutions[0];
            console.log("Moving chip to institution:", firstInstitution);
            this.moveChipToObject(firstInstitution);
        }
        // Hide menu
        const menu = document.getElementById("chipAssignmentMenu");
        if (menu) menu.style.display = "none";
    };

    private toNativeArray(observable: any): any[] {
        if (!observable) return [];
        if (Array.isArray(observable)) return observable;
        if (typeof observable.toJSON === "function") return observable.toJSON();
        return Array.from(observable);
    }

    private moveChipToObject(targetObject: any) {
        const chip = this.currentDraggedChip || this.currentSelectedChip;
        if (!chip) {
            console.log("No chip to move");
            return;
        }

        // Enforce max 1 per type
        if (this.hasChipOfType(targetObject, chip.type)) {
            console.log(`Target already has a chip of type '${chip.type}', ignoring drop`);
            this.currentDraggedChip = null;
            this.currentSelectedChip = null;
            return;
        }

        console.log("Moving -> chip:", chip, "to target:", targetObject);

        // Remove chip from source first
        const removed = this.removeChipFromSource(chip);
        console.log("Chip removed from source:", removed);
        
        if (!removed) {
            console.log("Chip was not removed, aborting move");
            return;
        }

        // Add to target using Kendo-safe access
        const targetContactData = this.toNativeArray(targetObject.get ? targetObject.get("contactData") : targetObject.contactData);
        console.log("Current contactData before push:", targetContactData);
        targetContactData.push(chip);
        console.log("contactData after push:", targetContactData);
        if (targetObject.set) {
            targetObject.set("contactData", targetContactData);
            console.log("Set contactData on target via .set(), new value:", targetObject.get("contactData"));
        } else {
            targetObject.contactData = targetContactData;
            console.log("Set contactData on target via direct assignment");
        }

        // Trigger update based on target type
        const hasFirstName = targetObject.get ? targetObject.get("firstName") !== undefined : "firstName" in targetObject;
        const hasName = targetObject.get ? targetObject.get("name") !== undefined : "name" in targetObject;

        console.log("Target type check: hasFirstName=", hasFirstName, ", hasName=", hasName);

        if (hasFirstName) {
            console.log("Triggering deepCopyPersons");
            const persons = this.toNativeArray(this.get("parsedPersons"));
            console.log("Persons array for deep copy:", persons);
            const newPersons = this.deepCopyPersons(persons);
            console.log("Deep copied persons:", newPersons);
            this.set("parsedPersons", newPersons);
        } else if (hasName) {
            console.log("Triggering deepCopyInstitutions");
            const institutions = this.toNativeArray(this.get("parsedInstitutions"));
            console.log("Institutions array for deep copy:", institutions);
            const newInstitutions = this.deepCopyInstitutions(institutions);
            console.log("Deep copied institutions:", newInstitutions);
            this.set("parsedInstitutions", newInstitutions);
        } else {
            console.log("WARNING: Could not determine target type for deep copy!");
        }

        // Clear selection
        this.currentDraggedChip = null;
        this.currentSelectedChip = null;
    }

    private toPlainObject(obj: any): any {
        if (!obj) return obj;
        if (typeof obj.toJSON === "function") return obj.toJSON();
        return obj;
    }

    private hasChipOfType(targetObject: any, chipType: string): boolean {
        const contactData = this.toNativeArray(
            targetObject.get ? targetObject.get("contactData") : targetObject.contactData
        );
        return contactData.some((c: any) => c.type === chipType);
    }

    public getUsedContactTypes(entity: any): Set<string> {
        const contactData = this.toNativeArray(
            entity && entity.get ? entity.get("contactData") : (entity ? entity.contactData : null)
        );
        return new Set(contactData.map((c: any) => c.type));
    }

    public allContactTypesFilled(entity: any): boolean {
        const used = this.getUsedContactTypes(entity);
        return used.has("email") && used.has("phone") && used.has("url");
    }

    private removeChipFromSource(chip: any): boolean {
        console.log("Attempting to remove chip:", chip);

        // Try removing from unassigned pool
        const pool = this.toNativeArray(this.get("unassignedContactData"));
        const poolIndex = pool.findIndex((c: any) => c.value === chip.value && c.type === chip.type);
        if (poolIndex !== -1) {
            pool.splice(poolIndex, 1);
            this.set("unassignedContactData", pool);
            console.log("Removed from pool");
            return true;
        }

        // Try removing from persons
        const persons = this.toNativeArray(this.get("parsedPersons"));
        for (const person of persons) {
            const contactData = this.toNativeArray(person.get ? person.get("contactData") : person.contactData);
            const index = contactData.findIndex((c: any) => c.value === chip.value && c.type === chip.type);
            if (index !== -1) {
                contactData.splice(index, 1);
                if (person.set) person.set("contactData", contactData);
                else person.contactData = contactData;
                this.set("parsedPersons", this.deepCopyPersons(persons));
                console.log("Removed from person");
                return true;
            }
        }

        // Try removing from institutions
        const institutions = this.toNativeArray(this.get("parsedInstitutions"));
        for (const institution of institutions) {
            const contactData = this.toNativeArray(institution.get ? institution.get("contactData") : institution.contactData);
            const index = contactData.findIndex((c: any) => c.value === chip.value && c.type === chip.type);
            if (index !== -1) {
                contactData.splice(index, 1);
                if (institution.set) institution.set("contactData", contactData);
                else institution.contactData = contactData;
                this.set("parsedInstitutions", this.deepCopyInstitutions(institutions));
                console.log("Removed from institution");
                return true;
            }
        }

        console.log("Chip not found in any source");
        return false;
    }

    // === Remove Actions ===

    public removeParsedPerson = (e: any) => {
        const personToRemove = e.data;
        const personsObservable = this.get<any>("parsedPersons");
        const persons = personsObservable ? Array.from(personsObservable) : [];
        
        // Move contactData back to pool before removing
        if (personToRemove.contactData && personToRemove.contactData.length > 0) {
            const poolObservable = this.get<any>("unassignedContactData");
            const pool = poolObservable ? Array.from(poolObservable) : [];
            pool.push(...personToRemove.contactData);
            this.set("unassignedContactData", pool);
        }
        
        const index = persons.indexOf(personToRemove);
        if (index !== -1) {
            persons.splice(index, 1);
            this.set("parsedPersons", persons);
            
        }
        
        this.updateCanCreateContact();
    };

    public removeParsedInstitution = (e: any) => {
        const institutionToRemove = e.data;
        const institutionsObservable = this.get<any>("parsedInstitutions");
        const institutions = institutionsObservable ? Array.from(institutionsObservable) : [];
        
        // Move contactData back to pool before removing
        if (institutionToRemove.contactData && institutionToRemove.contactData.length > 0) {
            const poolObservable = this.get<any>("unassignedContactData");
            const pool = poolObservable ? Array.from(poolObservable) : [];
            pool.push(...institutionToRemove.contactData);
            this.set("unassignedContactData", pool);
        }
        
        const index = institutions.indexOf(institutionToRemove);
        if (index !== -1) {
            institutions.splice(index, 1);
            this.set("parsedInstitutions", [...institutions]);
            
            // Update hasInstitution flag on all persons
            const persons = this.toNativeArray(this.get("parsedPersons"));
            const hasInstitution = institutions.length > 0;
            persons.forEach((p: any) => {
                p.hasInstitution = hasInstitution;
            });
            // Trigger re-render with deep copy to preserve all properties
            this.set("parsedPersons", this.deepCopyPersons(persons));
        }
        
        this.updateCanCreateContact();
    };

    public removeContactFromPerson = (e: any) => {
        const chipToRemove = e.data;
        const persons = this.toNativeArray(this.get("parsedPersons"));
        
        for (const person of persons) {
            const contactData = this.toNativeArray(person.get ? person.get("contactData") : person.contactData);
            const index = contactData.findIndex((c: any) => c.value === chipToRemove.value && c.type === chipToRemove.type);
            if (index !== -1) {
                contactData.splice(index, 1);
                if (person.set) person.set("contactData", contactData);
                else person.contactData = contactData;
                
                // Move to pool
                const pool = this.toNativeArray(this.get("unassignedContactData"));
                pool.push(chipToRemove);
                this.set("unassignedContactData", pool);
                
                this.set("parsedPersons", this.deepCopyPersons(persons));
                break;
            }
        }
    };

    public removeContactFromInstitution = (e: any) => {
        const chipToRemove = e.data;
        const institutions = this.toNativeArray(this.get("parsedInstitutions"));
        
        for (const institution of institutions) {
            const contactData = this.toNativeArray(institution.get ? institution.get("contactData") : institution.contactData);
            const index = contactData.findIndex((c: any) => c.value === chipToRemove.value && c.type === chipToRemove.type);
            if (index !== -1) {
                contactData.splice(index, 1);
                if (institution.set) institution.set("contactData", contactData);
                else institution.contactData = contactData;
                
                // Move to pool
                const pool = this.toNativeArray(this.get("unassignedContactData"));
                pool.push(chipToRemove);
                this.set("unassignedContactData", pool);
                
                this.set("parsedInstitutions", this.deepCopyInstitutions(institutions));
                break;
            }
        }
    };

    public addEmptyPerson = () => {
        const persons = this.toNativeArray(this.get("parsedPersons"));
        const placeholders = {
            title: this.t("title"),
            firstName: this.t("first-name"),
            lastName: this.t("last-name"),
            institutionName: this.t("institution-name"),
            street: this.t("street"),
            houseNumber: this.t("house-number"),
            postcode: this.t("postcode"),
            city: this.t("city"),
        };
        const labels = {
            person: this.t("person"),
            institution: this.t("institution"),
            relation: this.t("relation"),
            sexUnspecified: this.t("sex-unspecified"),
            sexMale: this.t("sex-male"),
            sexFemale: this.t("sex-female"),
        };
        const institutions = this.toNativeArray(this.get("parsedInstitutions"));
        const hasInstitution = institutions.length > 0;
        persons.push({
            title: "",
            firstName: "",
            lastName: "",
            sex: 0,
            contactData: [],
            placeholders: placeholders,
            labels: labels,
            address: null,
            hasInstitution: hasInstitution
        });
        
        // Load relation types before setting persons if institution exists
        if (hasInstitution) {
            this.loadRelationTypes().then(() => {
                this.set("parsedPersons", [...persons]);
                this.updateCanCreateContact();
            });
        } else {
            this.set("parsedPersons", [...persons]);
            this.updateCanCreateContact();
        }
    };

    public addEmptyInstitution = () => {
        const institutions = this.toNativeArray(this.get("parsedInstitutions"));
        const placeholders = {
            title: this.t("title"),
            firstName: this.t("first-name"),
            lastName: this.t("last-name"),
            institutionName: this.t("institution-name"),
            street: this.t("street"),
            houseNumber: this.t("house-number"),
            postcode: this.t("postcode"),
            city: this.t("city"),
        };
        const labels = {
            person: this.t("person"),
            institution: this.t("institution"),
            relation: this.t("relation"),
            sexUnspecified: this.t("sex-unspecified"),
            sexMale: this.t("sex-male"),
            sexFemale: this.t("sex-female"),
        };
        institutions.push({
            name: "",
            contactData: [],
            placeholders: placeholders,
            labels: labels,
            address: null,
        });
        this.set("parsedInstitutions", [...institutions]);
        
        // Update hasInstitution flag on all persons and load relation types
        const persons = this.toNativeArray(this.get("parsedPersons"));
        persons.forEach((p: any) => {
            p.hasInstitution = true;
        });
        
        // Load relation types BEFORE re-setting persons
        if (persons.length > 0) {
            this.loadRelationTypes().then(() => {
                this.set("parsedPersons", this.deepCopyPersons(persons));
                this.updateCanCreateContact();
            });
        } else {
            this.updateCanCreateContact();
        }
    };

    public addAddress = (e: any) => {
        // Get the data item from Kendo binding context
        const item = e.data;
        if (item) {
            // Initialize empty address
            item.set("address", {
                street: "",
                houseNumber: "",
                postcode: "",
                city: ""
            });
        }
    };

    public updateCanCreateContact = () => {
        // No longer needed - button is always enabled
        // Validation happens on click in createContactFromReview
    };

    private validateContactData(): string | null {
        const persons = this.toNativeArray(this.get("parsedPersons"));
        const institutions = this.toNativeArray(this.get("parsedInstitutions"));

        // At least one entity must exist
        if (persons.length === 0 && institutions.length === 0) {
            return this.t("validation-contact-required");
        }

        // If person exists, it must have firstName AND lastName
        if (persons.length > 0) {
            const person = persons[0];
            const firstName = this.getVal(person, "firstName");
            const lastName = this.getVal(person, "lastName");
            
            if (!firstName || !firstName.trim()) {
                return this.t("validation-firstname-required");
            }
            if (!lastName || !lastName.trim()) {
                return this.t("validation-lastname-required");
            }
        }

        // If institution exists, it must have name
        if (institutions.length > 0) {
            const institution = institutions[0];
            const name = this.getVal(institution, "name");
            
            if (!name || !name.trim()) {
                return this.t("validation-institution-name-required");
            }
        }

        return null; // All validations passed
    };

    // === Inline Contact Data Form ===

    public setupInlineFormDelegation = () => {
        const self = this;
        
        // Toggle inline form (Person/Institution) - exclude relation
        $(document).on("click", ".add-contact-inline-trigger:not([data-context='relation'])", function(e) {
            e.stopPropagation();
            const context = $(this).data("context");
            const dropZone = $(this).closest(".drop-zone");
            const form = dropZone.find(".add-contact-inline-form");
            const input = form.find(".inline-contact-input");
            
            // Determine which types are already used in this entity
            let entity: any = null;
            if (context === "person") {
                const persons = self.get<any[]>("parsedPersons");
                if (persons && persons.length > 0) entity = persons[0];
            } else if (context === "institution") {
                const institutions = self.get<any[]>("parsedInstitutions");
                if (institutions && institutions.length > 0) entity = institutions[0];
            }
            
            const usedTypes = entity ? self.getUsedContactTypes(entity) : new Set<string>();
            const allTypes = ["email", "phone", "url"];
            const availableTypes = allTypes.filter(t => !usedTypes.has(t));
            if (availableTypes.length === 0) return;
            
            const defaultType = availableTypes[0];
            
            // Store context and show form
            self.set("currentInlineContext", context);
            self.set("currentInlineType", defaultType);
            
            // Toggle visibility
            if (form.is(":visible")) {
                form.hide();
                input.val("");
            } else {
                // Hide all other forms first
                $(".add-contact-inline-form").hide();
                $(".inline-contact-input").val("");
                
                // Show/hide type buttons based on availability
                form.find(".contact-type-btn").each(function() {
                    const btnType = $(this).data("type");
                    if (usedTypes.has(btnType)) {
                        $(this).hide();
                    } else {
                        $(this).show();
                    }
                });
                
                form.show();
                input.focus();
                
                // Set active type button
                form.find(".contact-type-btn").removeClass("active");
                form.find(".contact-type-btn[data-type='" + defaultType + "']").addClass("active");
            }
        });
        
        // Select inline type (Person/Institution)
        $(document).on("click", ".add-contact-inline-form .contact-type-btn", function(e) {
            e.stopPropagation();
            const type = $(this).data("type");
            if (type) {
                self.set("currentInlineType", type);
                $(this).closest(".add-contact-inline-form").find(".contact-type-btn").removeClass("active");
                $(this).addClass("active");
            }
        });
        
        // Add inline contact (Person/Institution)
        $(document).on("click", ".add-contact-inline-form button[data-context]", function(e) {
            e.stopPropagation();
            const icon = $(this).find("i.fa-check");
            if (icon.length === 0) return; // Not the confirm button
            
            const context = $(this).data("context") || self.get("currentInlineContext");
            const form = $(this).closest(".add-contact-inline-form");
            const input = form.find(".inline-contact-input");
            const value = input.val() as string;
            
            if (!value || value.trim().length === 0) return;
            
            self.addInlineContactData(context, value.trim());
            
            // Reset and hide
            input.val("");
            form.hide();
        });
        
        // Cancel inline form (Person/Institution)
        $(document).on("click", ".add-contact-inline-form button:not([data-context])", function(e) {
            e.stopPropagation();
            const icon = $(this).find("i.fa-times");
            if (icon.length === 0) return; // Not the cancel button
            
            const form = $(this).closest(".add-contact-inline-form");
            const input = form.find(".inline-contact-input");
            input.val("");
            form.hide();
        });
        
        // Enter key in inline input (Person/Institution)
        $(document).on("keypress", ".inline-contact-input", function(e) {
            if (e.key === "Enter" || e.keyCode === 13) {
                const context = $(this).data("context") || self.get("currentInlineContext");
                const value = $(this).val() as string;
                
                if (!value || value.trim().length === 0) return;
                
                self.addInlineContactData(context, value.trim());
                
                // Reset and hide
                $(this).val("");
                $(this).closest(".add-contact-inline-form").hide();
            }
        });
    };

    private addInlineContactData(context: string, value: string) {
        const type = this.get<string>("currentInlineType");
        const iconMap: { [key: string]: string } = {
            email: "fa-envelope",
            phone: "fa-phone",
            url: "fa-globe",
        };

        const newChip = {
            type: type,
            value: value,
            displayValue: value,
            icon: iconMap[type] || "fa-circle",
        };

        if (context === "person") {
            const persons = this.get<any[]>("parsedPersons");
            if (persons && persons.length > 0) {
                if (this.hasChipOfType(persons[0], type)) return;
                if (!persons[0].contactData) {
                    persons[0].contactData = [];
                }
                persons[0].contactData.push(newChip);
                this.set("parsedPersons", this.deepCopyPersons(persons));
            }
        } else if (context === "institution") {
            const institutions = this.get<any[]>("parsedInstitutions");
            if (institutions && institutions.length > 0) {
                if (this.hasChipOfType(institutions[0], type)) return;
                if (!institutions[0].contactData) {
                    institutions[0].contactData = [];
                }
                institutions[0].contactData.push(newChip);
                this.set("parsedInstitutions", this.deepCopyInstitutions(institutions));
            }
        }
    }

    private getVal(obj: any, key: string): any {
        return obj.get ? obj.get(key) : obj[key];
    }

    private deepCopyPersons(persons: any[]): any[] {
        return persons.map(p => ({
            title: this.getVal(p, "title") || "",
            firstName: this.getVal(p, "firstName") || "",
            lastName: this.getVal(p, "lastName") || "",
            sex: this.getVal(p, "sex") ?? 0,
            contactData: this.toNativeArray(this.getVal(p, "contactData")),
            placeholders: this.getVal(p, "placeholders"),
            labels: this.getVal(p, "labels"),
            address: this.getVal(p, "address"),
            hasInstitution: this.getVal(p, "hasInstitution") || false,
            showInlineForm: this.getVal(p, "showInlineForm") || false
        }));
    }

    private deepCopyInstitutions(institutions: any[]): any[] {
        return institutions.map(i => ({
            name: this.getVal(i, "name") || "",
            contactData: this.toNativeArray(this.getVal(i, "contactData")),
            placeholders: this.getVal(i, "placeholders"),
            labels: this.getVal(i, "labels"),
            address: this.getVal(i, "address"),
            showInlineForm: this.getVal(i, "showInlineForm") || false
        }));
    }

    // === Create Contact ===

    private extractContactValue(contactData: any[], type: string): string | null {
        const chip = contactData.find((c: any) => c.type === type);
        return chip ? chip.value : null;
    }

    private extractAddress(entity: any): any | null {
        const addr = this.getVal(entity, "address");
        if (!addr) return null;
        const a = this.toPlainObject(addr);
        const hasValue = a.street || a.houseNumber || a.postcode || a.city;
        if (!hasValue) return null;
        return {
            street: a.street || "",
            houseNumber: a.houseNumber || "",
            postcode: a.postcode || "",
            city: a.city || ""
        };
    }

    public buildResultJson(): any {
        const persons = this.toNativeArray(this.get("parsedPersons"));
        const institutions = this.toNativeArray(this.get("parsedInstitutions"));
        const selectedRelationTypeId = this.get<string>("selectedRelationTypeId");
        const selectedFormalityId = this.get<string>("selectedFormalityId");

        let person: any = null;
        let institution: any = null;

        if (persons.length > 0) {
            const p = persons[0];
            const title = this.getVal(p, "title") || "";
            const first = this.getVal(p, "firstName") || "";
            const last = this.getVal(p, "lastName") || "";
            const sex = this.getVal(p, "sex") ?? 0;
            const contactData = this.toNativeArray(this.getVal(p, "contactData"));

            person = {
                full: [title, first, last].filter(Boolean).join(" ").trim(),
                title: title,
                first: first,
                middle: "",
                last: last,
                suffix: "",
                sex: sex,
                formalityId: selectedFormalityId ? parseInt(selectedFormalityId, 10) : null,
                email: this.extractContactValue(contactData, "email"),
                phone: this.extractContactValue(contactData, "phone"),
                url: this.extractContactValue(contactData, "url"),
                address: this.extractAddress(p),
                relationTypeId: selectedRelationTypeId ? parseInt(selectedRelationTypeId, 10) : null
            };
        }

        if (institutions.length > 0) {
            const i = institutions[0];
            const contactData = this.toNativeArray(this.getVal(i, "contactData"));
            institution = {
                name: this.getVal(i, "name") || "",
                email: this.extractContactValue(contactData, "email"),
                phone: this.extractContactValue(contactData, "phone"),
                url: this.extractContactValue(contactData, "url"),
                address: this.extractAddress(i)
            };
        }

        return {
            person: person,
            institution: institution
        };
    }

    public createContactFromReview = async () => {
        try {
            // Validate before submitting
            const validationError = this.validateContactData();
            if (validationError) {
                this.showNotification(validationError, "error");
                return;
            }
            
            Util.showLoading();
            
            const contactData = this.buildResultJson();
            const response = await this.addressParserService.createContact(contactData);
            
            if (response.success) {
                // Hide review dialog
                this.set("createContactReviewVisible", false);
                
                // Notify success
                if (response.message) {
                    this.showNotification(response.message, "success");
                } else {
                    this.showNotification(this.t("contact-created-successfully"), "success");
                }
                
                // Call callback to open the created contact
                if (this.onContactCreated) {
                    await this.onContactCreated(response.personId, response.institutionId);
                }
            } else {
                // Show error message
                this.showNotification(response.message || this.t("error-creating-contact"), "error");
            }
            
            Util.hideLoading();
        } catch (error) {
            Util.hideLoading();
            console.error("Error creating contact:", error);
            this.showNotification(error.message || this.t("error-creating-contact"), "error");
        }
    };
}

